import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { API_URLS, API_CONFIG } from '../../config/api';
import { useFadeInUp } from '../../hooks/useFadeInUp';
import { 
  BookOpen, 
  Clock, 
  User, 
  Eye,
  Heart,
  Share2,
  Search,
  TrendingUp,
  Calendar,
  ArrowRight,
  ShoppingCart,
  Globe,
  Server,
  Bot,
  Zap,
  Star,
  MessageSquare,
  Target,
  Award,
  Lightbulb,
  Rocket,
  Filter
} from 'lucide-react';

// Kategori ismini düzgün formata çevir (Türkçe karakter desteği)
const formatCategoryName = (categoryName) => {
  if (!categoryName) return 'Genel';
  
  // Önce lowercase yap, sonra kelimelere böl (tire veya boşlukla)
  const normalized = categoryName.toLowerCase();
  const words = normalized.split(/[-\s]+/);
  
  return words
    .map(word => {
      if (!word) return '';
      // Türkçe locale kullanarak baş harfi büyüt
      const firstChar = word.charAt(0);
      const rest = word.slice(1);
      // Türkçe locale ile büyük harfe çevir (i→İ, ı→I)
      const upperFirst = firstChar.toLocaleUpperCase('tr-TR');
      return upperFirst + rest;
    })
    .join(' ');
};

const BlogPage = () => {
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [visibleCount, setVisibleCount] = useState(6);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [contentReady, setContentReady] = useState(false);

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Animasyonlar
  const categoriesCardAnim = useFadeInUp(0.1, '0ms');
  const recentPostsCardAnim = useFadeInUp(0.1, '100ms');
  const newsletterCardAnim = useFadeInUp(0.1, '200ms');
  const filterBarAnim = useFadeInUp(0.1, '0ms');
  const featuredTitleAnim = useFadeInUp(0.1, '100ms');
  const featuredGridAnim = useFadeInUp(0.1, '200ms');
  const postsGridAnim = useFadeInUp(0.1, '300ms');

  const normalizeImageUrl = (url) => {
    if (!url) return '';
    const BASE_URL = API_CONFIG.BASE_URL;
    try {
      const u = String(url);
      // Rewrite any getScreenshot URL (absolute or relative) to direct uploads
      if (u.includes('getScreenshot') && u.includes('file=')) {
        const match = u.match(/file=([^&]+)/);
        if (match && match[1]) return `${BASE_URL}/uploads/${decodeURIComponent(match[1])}`;
      }
      if (u.startsWith('http://') || u.startsWith('https://')) return u;
      if (u.startsWith('/backend/uploads/')) return `${BASE_URL}${u.replace('/backend', '')}`;
      if (u.startsWith('/uploads/')) return `${BASE_URL}${u}`;
      if (u.startsWith('/blog/')) return `${BASE_URL}/uploads/${u.split('/').pop()}`;
      if (!u.startsWith('/')) return `${BASE_URL}/uploads/${u}`;
      return `${BASE_URL}${u}`;
    } catch { return url; }
  };

  const [categories, setCategories] = useState([
    { id: 'all', name: 'Tüm Makaleler', count: 0 }
  ]);

  const [featuredPosts, setFeaturedPosts] = useState([
    {
      id: 1,
      title: 'E-ticaret Sitesi Kurma Rehberi: 2024 Güncel Stratejileri',
      excerpt: 'E-ticaret dünyasına adım atmak isteyenler için kapsamlı rehber. Platform seçiminden ödeme sistemlerine kadar her şey.',
      category: formatCategoryName('e-ticaret'),
      author: 'Ahmet Yılmaz',
      authorRole: 'E-ticaret Uzmanı',
      publishDate: '2024-01-15',
      readTime: '12 dakika',
      views: 15420,
      likes: 324,
      featured: true,
      image: '/blog/ecommerce-2024.jpg',
      tags: ['e-ticaret', '2024 trendleri', 'dijital pazarlama']
    },
    {
      id: 2,
      title: 'ChatGPT ile İş Süreçlerini Otomatikleştirme: Pratik Örnekler',
      excerpt: 'Yapay zeka araçları ile iş süreçlerinizi nasıl otomatikleştireceğinizi adım adım öğrenin.',
      category: formatCategoryName('yapay-zeka'),
      author: 'Dr. Elif Yıldız',
      authorRole: 'AI Uzmanı',
      publishDate: '2024-01-20',
      readTime: '15 dakika',
      views: 12800,
      likes: 298,
      featured: true,
      image: '/blog/ai-automation.jpg',
      tags: ['ai', 'otomasyon', 'chatgpt']
    }
  ]);

  const [blogPosts, setBlogPosts] = useState([
    {
      id: 3,
      title: 'E-ihracat ile Global Pazarlara Açılma: Başarı Hikayeleri',
      excerpt: 'Türkiye\'den dünyaya satış yapan işletmelerin deneyimlerinden öğrenebileceğiniz stratejiler.',
      category: formatCategoryName('e-ihracat'),
      author: 'Mehmet Özkan',
      authorRole: 'E-ihracat Danışmanı',
      publishDate: '2024-01-18',
      readTime: '10 dakika',
      views: 8640,
      likes: 156,
      image: '/blog/eexport-success.jpg',
      tags: ['e-ihracat', 'global pazarlama', 'başarı hikayesi']
    },
    {
      id: 4,
      title: 'Web Hosting Seçimi: 2024 Performans Karşılaştırması',
      excerpt: 'En popüler hosting sağlayıcılarının detaylı karşılaştırması ve hangi kriterlere dikkat edilmeli.',
      category: formatCategoryName('hosting'),
      author: 'Can Demir',
      authorRole: 'Sistem Yöneticisi',
      publishDate: '2024-01-16',
      readTime: '8 dakika',
      views: 7320,
      likes: 134,
      image: '/blog/hosting-comparison.jpg',
      tags: ['hosting', 'performans', 'karşılaştırma']
    },
    {
      id: 5,
      title: 'Shopify vs WooCommerce: 2024 Detaylı Karşılaştırma',
      excerpt: 'İki popüler e-ticaret platformunun artı ve eksileri, hangi durumda hangisi tercih edilmeli?',
      category: formatCategoryName('e-ticaret'),
      author: 'Ayşe Kaya',
      authorRole: 'E-ticaret Uzmanı',
      publishDate: '2024-01-14',
      readTime: '11 dakika',
      views: 6890,
      likes: 167,
      image: '/blog/shopify-vs-woocommerce.jpg',
      tags: ['shopify', 'woocommerce', 'platform karşılaştırma']
    },
    {
      id: 6,
      title: 'Yapay Zeka ile İçerik Üretimi: En İyi Araçlar',
      excerpt: 'Blog yazılarından sosyal medya gönderilerine kadar AI ile içerik üretmenin yolları.',
      category: formatCategoryName('yapay-zeka'),
      author: 'Emre Şahin',
      authorRole: 'Content Creator',
      publishDate: '2024-01-12',
      readTime: '13 dakika',
      views: 9120,
      likes: 203,
      image: '/blog/ai-content-creation.jpg',
      tags: ['ai', 'içerik üretimi', 'blog yazımı']
    }
  ]);

  // Check URL parameters for category filtering
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const categoryParam = urlParams.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [location.search]);

  // Fetch blogs from API and hydrate lists
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await fetch(API_URLS.BLOGS);
        if (!res.ok) {
          console.log('Blog API request failed:', res.status);
          return; // keep fallback
        }
        const data = await res.json();
        // Handle different response formats
        const posts = Array.isArray(data) ? data : (data.blogs || data.data || []);
        if (!Array.isArray(posts) || posts.length === 0) {
          return;
        }
        // Map API fields to UI
        const mapped = posts.map(p => {
          const rawCategorySlug = p.category || 'genel';
          // Use category_name from API if available, otherwise format the slug
          const categoryName = p.category_name || formatCategoryName(rawCategorySlug);
          
          return {
            id: p.id || p.slug,
            slug: p.slug || p.id,
            title: p.title,
            excerpt: p.excerpt || '',
            category: categoryName,
            author: p.author || 'Editör',
            authorRole: p.author_role || '',
            publishDate: p.published_at || p.publishDate,
            readTime: p.read_time || '5 dakika',
            views: Number(p.views || 0),
            likes: Number(p.likes || 0),
            image: p.image || '',
            tags: Array.isArray(p.tags) ? p.tags : [],
            category_slug: rawCategorySlug.toLowerCase().replace(/[^a-z0-9çğıöşü\-\s]/gi, '').replace(/\s+/g, '-')
          };
        });
        setBlogPosts(mapped);
        // Select featured as top 2 by views
        const top = [...mapped].sort((a,b)=>b.views-a.views).slice(0,2);
        setFeaturedPosts(top);
        // Build full category list dynamically
        const counts = mapped.reduce((acc, p) => {
          const key = p.category_slug || 'genel';
          const categoryName = p.category; // Already formatted from API
          if (!acc[key]) acc[key] = { name: categoryName, count: 0 };
          acc[key].count += 1;
          return acc;
        }, {});
        const builtCategories = [
          { id: 'all', name: 'Tüm Makaleler', count: mapped.length },
          ...Object.entries(counts).map(([slug, info]) => ({ id: slug, name: info.name, count: info.count }))
        ];
        setCategories(builtCategories);
      } catch (e) {
        // ignore failures
      }
    };
    fetchBlogs();
  }, []);

  const recentPosts = blogPosts.slice(0, 4);

  const popularTags = [
    'E-ticaret', 'AI', 'Hosting', 'SEO', 'WordPress', 'Shopify', 'E-ihracat',
    'Dijital Pazarlama', 'Web Tasarım', 'Mobil Uygulama', 'Sunucu Yönetimi'
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    // Arama functionality buraya eklenecek
  };

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    setNewsletterLoading(true);
    setNewsletterMessage('');
    try {
      const res = await fetch(API_URLS.NEWSLETTER_SUBSCRIBE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newsletterEmail })
      });
      const raw = await res.text();
      let data;
      try { data = JSON.parse(raw); } catch { data = { success: res.ok, message: raw }; }
      if (res.ok && (data.success || data.status === 'success' || data.status === 'ok' || data.status === 'exists')) {
        setNewsletterMessage('Başarıyla abone olundu.');
        setNewsletterEmail('');
      } else if (data.status === 'exists') {
        setNewsletterMessage('Zaten abonesiniz.');
      } else {
        setNewsletterMessage(data.message || 'Abonelik başarısız.');
      }
    } catch (err) {
      setNewsletterMessage('Abonelik sırasında hata oluştu.');
    } finally {
      setNewsletterLoading(false);
    }
  };

  const featuredIds = new Set((featuredPosts || []).map(p => p.id || p.slug));
  const filteredPosts = [...blogPosts].filter(post => {
    const matchesCategory = selectedCategory === 'all' || post.category_slug === selectedCategory;
    
    const matchesSearch = !searchQuery || 
                         post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const notDuplicateWhenAll = !(selectedCategory === 'all' && featuredIds.has(post.id || post.slug));
    return matchesCategory && matchesSearch && notDuplicateWhenAll;
  });

  const sortedPosts = [...filteredPosts].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.publishDate) - new Date(a.publishDate);
      case 'oldest':
        return new Date(a.publishDate) - new Date(b.publishDate);
      case 'popular':
        return b.views - a.views;
      case 'liked':
        return b.likes - a.likes;
      default:
        return 0;
    }
  });
  const visiblePosts = sortedPosts.slice(0, visibleCount);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section 
        className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-16"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Blog</h1>
            <p className="text-xl text-purple-100 mb-8">
              Teknoloji, iş ve dijital dünya hakkında güncel içerikler
            </p>
            <form onSubmit={handleSearch} className="flex justify-center">
              <div className="relative w-full max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white w-5 h-5 z-10" />
                <Input
                  type="text"
                  placeholder="Blog yazısı ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-16 py-3 w-full rounded-lg border-0 bg-white/10 backdrop-blur-sm text-white placeholder-white focus:bg-white/20 focus:ring-2 focus:ring-white/30"
                />
                <Button
                  type="submit"
                  size="sm"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white h-8 px-4 text-sm border border-white/30 backdrop-blur-sm"
                >
                  Ara
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
          {/* Sidebar Left - Desktop Only */}
          <div className="hidden lg:block space-y-8">
            {/* Categories (vertical) */}
            <Card ref={categoriesCardAnim.ref} className={`shadow-lg ${categoriesCardAnim.className}`} style={categoriesCardAnim.style}>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Filter className="w-5 h-5 text-purple-600" />
                  <span>Kategoriler</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col divide-y">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`flex items-center justify-between py-3 text-left ${selectedCategory === cat.id ? 'text-purple-600 font-semibold' : 'text-gray-700 hover:text-purple-600'}`}
                    >
                      <span>{cat.name}</span>
                      <Badge variant="secondary" className="ml-2">{cat.count}</Badge>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Posts */}
            <Card ref={recentPostsCardAnim.ref} className={`shadow-lg ${recentPostsCardAnim.className}`} style={recentPostsCardAnim.style}>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  <span>Son Makaleler</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {recentPosts.map((post) => (
                  <Link key={post.id} to={`/blog/${post.slug || post.id}`} className="flex space-x-3 group cursor-pointer">
                    <div className="w-16 h-16 rounded-lg flex-shrink-0 overflow-hidden bg-gradient-to-br from-purple-500 to-blue-600 relative">
                      {post.image && (
                        <img
                          src={normalizeImageUrl(post.image)}
                          alt={post.title}
                          className="w-full h-full object-cover object-top"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      )}
                      <div className="absolute inset-0 bg-black/30 z-10"></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900 group-hover:text-purple-600 transition-colors line-clamp-2">
                        {post.title}
                      </h4>
                      <div className="flex items-center text-xs text-gray-500 mt-2">
                        <Clock className="w-3 h-3 mr-1" />
                        <span>{post.readTime}</span>
                        <span className="mx-2">•</span>
                        <Eye className="w-3 h-3 mr-1" />
                        <span>{post.views.toLocaleString()}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </CardContent>
            </Card>

            {/* Newsletter */}
            <Card ref={newsletterCardAnim.ref} className={`shadow-lg bg-gradient-to-br from-purple-600 to-blue-600 text-white newsletter ${newsletterCardAnim.className}`} style={newsletterCardAnim.style}>
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2">Haftalık Bülten</h3>
                <p className="text-purple-100 text-sm mb-6">
                  En güncel makaleler ve dijital pazarlama ipuçları e-postanızda
                </p>
                <form onSubmit={handleNewsletter} className="space-y-3">
                  <Input
                    type="email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="E-posta adresiniz"
                    className="bg-white/10 border-white/20 text-white placeholder-white"
                    autoComplete="email"
                    required
                  />
                  <Button type="submit" disabled={newsletterLoading} className="w-full bg-white text-purple-600 hover:bg-gray-100">
                    <Rocket className="w-4 h-4 mr-2" />
                    {newsletterLoading ? 'Gönderiliyor...' : 'Abone Ol'}
                  </Button>
                  {newsletterMessage && (
                    <p className="text-xs text-white/90">{newsletterMessage}</p>
                  )}
                </form>
                <p className="text-xs text-purple-200 mt-4">
                  📧 15.000+ aktif abone • ⭐ 4.8 memnuniyet
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Mobile Filters - Wrap Layout */}
          <div className="lg:hidden space-y-4 mb-6">
            {/* Mobile Categories */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2 px-1">Kategoriler</h3>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedCategory === cat.id
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span>{cat.name}</span>
                    <Badge variant="secondary" className={selectedCategory === cat.id ? 'bg-white text-purple-600' : 'bg-white'}>
                      {cat.count}
                    </Badge>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Filter and Sort */}
            <div ref={filterBarAnim.ref} className={`flex flex-col sm:flex-row justify-between items-center mb-8 space-y-4 sm:space-y-0 ${filterBarAnim.className}`} style={filterBarAnim.style}>
              <h2 className="text-2xl font-bold text-gray-900">
                {selectedCategory === 'all' ? 'Tüm Makaleler' : categories.find(c => c.id === selectedCategory)?.name} 
                <span className="text-gray-500 text-lg ml-2">({sortedPosts.length})</span>
              </h2>
              
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="newest">En Yeni</option>
                    <option value="oldest">En Eski</option>
                    <option value="popular">En Popüler</option>
                    <option value="liked">En Beğenilen</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Featured Posts */}
            {selectedCategory === 'all' && (
              <div className="mb-12">
                <h3 ref={featuredTitleAnim.ref} className={`text-2xl font-bold text-gray-900 mb-6 flex items-center ${featuredTitleAnim.className}`} style={featuredTitleAnim.style}>
                  <Star className="w-6 h-6 text-yellow-500 mr-2" />
                  Öne Çıkan Makaleler
                </h3>
                <div ref={featuredGridAnim.ref} className={`grid grid-cols-1 md:grid-cols-2 gap-8 ${featuredGridAnim.className}`} style={featuredGridAnim.style}>
                  {featuredPosts.map((post) => (
                    <Card key={post.id || post.slug} className="group overflow-hidden hover:shadow-2xl transition-all duration-300 border-0 bg-white">
                      {/* Post Image */}
                      <div className="relative h-64 bg-gradient-to-br from-purple-500 to-blue-600 overflow-hidden">
                        <div className="absolute inset-0 bg-black/30 z-10"></div>
                        {post.image && (
                          <img
                            src={normalizeImageUrl(post.image)}
                            alt={post.title}
                            className="absolute inset-0 w-full h-full object-cover object-top z-0"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        )}
                        <div className="absolute top-4 left-4 z-20">
                          <Badge className="bg-yellow-500 text-black font-bold">
                            ⭐ Öne Çıkan
                          </Badge>
                        </div>
                        <div className="absolute top-4 right-4 z-20">
                          <Badge variant="outline" className="bg-white/90 text-gray-800 border-0">
                            {post.category}
                          </Badge>
                        </div>
                        <div className="absolute bottom-4 left-4 right-4 z-20">
                          <h3 className="text-xl font-bold text-white mb-2 group-hover:text-yellow-400 transition-colors">
                            {post.title}
                          </h3>
                        </div>
                      </div>

                      <CardContent className="p-6">
                        {/* Meta Info */}
                        <div className="flex items-center text-sm text-gray-500 mb-3">
                          <User className="w-4 h-4 mr-1" />
                          <span className="mr-4">{post.author}</span>
                          <Clock className="w-4 h-4 mr-1" />
                          <span className="mr-4">{post.readTime}</span>
                          <Calendar className="w-4 h-4 mr-1" />
                          <span>{new Date(post.publishDate).toLocaleDateString('tr-TR')}</span>
                        </div>

                        {/* Excerpt */}
                        <p className="text-gray-600 mb-4 line-clamp-2">{post.excerpt}</p>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {post.tags.slice(0, 2).map((tag, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>

                        {/* Stats and Action */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span className="flex items-center space-x-1">
                              <Eye className="w-4 h-4" />
                              <span>{post.views.toLocaleString()}</span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <Heart className="w-4 h-4" />
                              <span>{post.likes}</span>
                            </span>
                          </div>
                          <Link to={`/blog/${post.slug || post.id}`}>
                            <Button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white">
                              Okumaya Devam Et
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Blog Posts Grid */}
            <div ref={postsGridAnim.ref} className={`space-y-8 ${postsGridAnim.className}`} style={postsGridAnim.style}>
              {visiblePosts.filter(post => !featuredIds.has(post.id || post.slug) || selectedCategory !== 'all').map((post) => (
                <Card key={post.id || post.slug} className="group hover:shadow-xl transition-all duration-300 overflow-hidden">
                  <div className="md:flex">
                    {/* Post Image */}
                    <div className="md:w-1/3">
                      <div className="relative h-48 md:h-full bg-gradient-to-br from-blue-500 to-purple-600">
                        <div className="absolute inset-0 bg-black/30 z-10"></div>
                        {post.image && (
                          <img
                            src={normalizeImageUrl(post.image)}
                            alt={post.title}
                            className="absolute inset-0 w-full h-full object-cover object-top z-0"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        )}
                        <div className="absolute top-4 left-4 z-20">
                          <Badge className="bg-white/90 text-gray-800">
                            {post.category}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {/* Post Content */}
                    <div className="md:w-2/3 p-6">
                      <div className="flex items-center text-sm text-gray-500 mb-3">
                        <User className="w-4 h-4 mr-1" />
                        <span className="mr-4">{post.author}</span>
                        <Clock className="w-4 h-4 mr-1" />
                        <span className="mr-4">{post.readTime}</span>
                        <Calendar className="w-4 h-4 mr-1" />
                        <span>{new Date(post.publishDate).toLocaleDateString('tr-TR')}</span>
                      </div>

                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-purple-600 transition-colors">
                        {post.title}
                      </h3>

                      <p className="text-gray-600 mb-4 line-clamp-2">{post.excerpt}</p>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.slice(0, 3).map((tag, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span className="flex items-center space-x-1">
                            <Eye className="w-4 h-4" />
                            <span>{post.views.toLocaleString()}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Heart className="w-4 h-4" />
                            <span>{post.likes}</span>
                          </span>
                          <Button variant="ghost" size="sm">
                            <Share2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <Link to={`/blog/${post.slug || post.id}`}>
                          <Button variant="outline" className="border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white">
                            Devamını Oku
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Load More */}
            {visibleCount < sortedPosts.length && (
              <div className="text-center mt-12">
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={() => setVisibleCount((c) => Math.min(c + 6, sortedPosts.length))}
                  className="border-2 border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white px-8"
                >
                  Daha Fazla Makale Yükle
                </Button>
              </div>
            )}
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default BlogPage; 